import type { ReactElement } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ApiError, api, type AuthUser } from "./lib/api";
import { ErrorBox, PageSpinner } from "./components/ui";
import LoginPage from "./pages/LoginPage";
import SetupPage from "./pages/SetupPage";
import SitesPage from "./pages/SitesPage";
import SiteLayout from "./pages/SiteLayout";
import OverviewPage from "./pages/OverviewPage";
import RealtimePage from "./pages/RealtimePage";
import EventsPage from "./pages/EventsPage";
import SettingsPage from "./pages/SettingsPage";
import SharePage from "./pages/SharePage";
import UsersPage from "./pages/UsersPage";

export function useMe() {
  return useQuery<AuthUser | null>({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        const res = await api.get<{ user: AuthUser }>("/api/auth/me");
        return res.user;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
  });
}

export function useAuthStatus() {
  return useQuery({
    queryKey: ["auth-status"],
    queryFn: () => api.get<{ needsSetup: boolean }>("/api/auth/status"),
    staleTime: 60000,
  });
}

function RequireAuth({ children }: { children: ReactElement }) {
  const { data: me, isLoading, error } = useMe();
  if (isLoading) return <PageSpinner />;
  if (error) return <ErrorBox message="Gagal memuat sesi. Muat ulang halaman." />;
  if (!me) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/setup" element={<SetupPage />} />
      <Route path="/share/:token" element={<SharePage />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <SitesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/users"
        element={
          <RequireAuth>
            <UsersPage />
          </RequireAuth>
        }
      />
      <Route
        path="/s/:siteId"
        element={
          <RequireAuth>
            <SiteLayout />
          </RequireAuth>
        }
      >
        <Route index element={<OverviewPage />} />
        <Route path="realtime" element={<RealtimePage />} />
        <Route path="events" element={<EventsPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
